import { useState, useCallback } from 'react';

export const useFileUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const allowedExtensions = [
    '.pdf', '.jpg', '.jpeg', '.png'
  ];

  const addFiles = useCallback((files) => {
    const newFiles = Array.from(files).filter(file => {
      const fileName = file.name.toLowerCase();
      const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));
      if (!isValid) {
        setError('Only PDF and Images are allowed');
        return false;
      }
      return true;
    });

    if (newFiles.length === 0) {
      return;
    }

    setError('');
    const filesWithId = newFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    }));

    setUploadedFiles(prev => [...prev, ...filesWithId]);
    return filesWithId;
  }, []);

  const removeFile = useCallback((fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const simulateUpload = useCallback((files, onComplete) => {
    setUploading(true);
    setUploadProgress(0);

    let completedFiles = 0;
    const totalFiles = files.length;

    files.forEach((fileObj) => {
      const interval = setInterval(() => {
        setUploadedFiles(prev => prev.map(f => {
          if (f.id === fileObj.id) {
            const newProgress = Math.min(f.progress + Math.random() * 20, 100);
            if (newProgress >= 100) {
              completedFiles++;
              setUploadProgress((completedFiles / totalFiles) * 100);
              if (completedFiles === totalFiles) {
                setUploading(false);
                onComplete?.();
              }
            }
            return {
              ...f,
              progress: newProgress,
              status: newProgress >= 100 ? 'completed' : 'uploading'
            };
          }
          return f;
        }));

        if (fileObj.progress >= 100) {
          clearInterval(interval);
        }
      }, 200 + Math.random() * 300);
    });
  }, []);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    uploadedFiles,
    uploading,
    uploadProgress,
    error,
    addFiles,
    removeFile,
    simulateUpload,
    clearError,
    setError,
    setUploading,
    setUploadedFiles
  };
};
